import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { FlaskConical, Sparkles } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { EmptyState } from '../atoms/EmptyState';
import { Heading } from '../atoms/Typography/Heading';
import { Inline } from '../atoms/Inline/Inline';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { InsightChips } from './InsightChips';
import { REEF_INTELLIGENCE_ANCHOR } from '../timeline/timelineEventText';
import { ExplainabilityModal } from './ExplainabilityModal';
import { ReefSuggestionList } from './ReefSuggestionList';
import { LightingInsightSection } from './LightingInsightSection';
import { FlowInsightSection } from './FlowInsightSection';
import { useReefInsight } from '../../hooks/useReefInsight';
import { classifyInsightError } from '../../services/insightService';
import type { InsightErrorKind, InsightResponse } from '../../services/insightService';
import { AiFeedbackControl } from '../aiFeedback/AiFeedbackControl';
import { AiUsageMeter } from '../aiUsage/AiUsageMeter';
import { AiLimitErrorMessage } from '../aiUsage/AiLimitNotice';
import { useAiUsage } from '../../hooks/useAiUsage';
import './ReefInsightCard.css';

/** Name of a language in the UI language ("Englisch"), or the code when unknown. */
function languageDisplayName(code: string, uiLocale: string): string {
  try {
    return new Intl.DisplayNames([uiLocale], { type: 'language' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** Monthly AI credits used up / app-wide AI budget reached. */
function isAiLimitKind(kind: InsightErrorKind): boolean {
  return kind === 'aiBudgetExceeded' || kind === 'aiUnavailable';
}

export interface ReefInsightCardProps {
  /** The dashboard's selected aquarium. */
  aquariumId: string | undefined;
}

interface PopulatedProps {
  aquariumId: string;
  result: InsightResponse;
  refreshError: unknown;
  /** The card's AiUsageMeter already explains used-up credits. */
  aiCreditsUsedUp: boolean;
}

function PopulatedInsight({ aquariumId, result, refreshError, aiCreditsUsedUp }: PopulatedProps) {
  const { t, i18n } = useTranslation();
  const [explainIndex, setExplainIndex] = useState<number | null>(null);
  const { insight } = result;
  const recommendationsId = useId();

  // The insight is stored in the language it was generated in; switching the
  // UI language never triggers a paid AI call on its own — "Refresh" regenerates.
  const uiLanguage = (i18n.language ?? 'en').split('-')[0];
  const insightLanguage = result.language ?? 'en';
  const languageMismatch = insightLanguage !== uiLanguage;
  const insightLanguageName = languageDisplayName(insightLanguage, i18n.language);

  const generatedAt = new Date(insight.generatedAt);
  const updatedLabel = Number.isNaN(generatedAt.getTime())
    ? null
    : new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(generatedAt);
  const dataAsOf = insight.dataAsOf ? new Date(insight.dataAsOf) : null;
  const basedOnLabel = dataAsOf === null || Number.isNaN(dataAsOf.getTime())
    ? null
    : new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(dataAsOf);

  return (
    <Stack gap="6">
      <Stack gap="2">
        {insight.summary !== '' && <Text lang={insightLanguage}>{insight.summary}</Text>}
        {updatedLabel !== null && (
          <Text size="sm" color="muted">
            {t('insights.reef.updatedAt', { time: updatedLabel })}
            {basedOnLabel !== null && ` · ${t('insights.reef.basedOn', { date: basedOnLabel })}`}
          </Text>
        )}
        {languageMismatch && (
          <Alert tone="info" compact live="off" data-testid="reef-insight-language-notice">
            {t('insights.reef.otherLanguage', { language: insightLanguageName })}
          </Alert>
        )}
        {result.stale === true && (
          <Alert tone="info" compact>
            {t('insights.reef.staleNotice')}
          </Alert>
        )}
        {refreshError != null && (
          isAiLimitKind(classifyInsightError(refreshError)) ? (
            <AiLimitErrorMessage error={refreshError} suppressExceeded={aiCreditsUsedUp} />
          ) : classifyInsightError(refreshError) === 'rateLimited' ? (
            <Alert tone="info" compact>
              {t('insights.reef.rateLimited')}
            </Alert>
          ) : (
            <Alert compact>
              {t('insights.reef.refreshFailed')}
            </Alert>
          )
        )}
      </Stack>

      <Stack gap="3" as="section" aria-labelledby={recommendationsId}>
        <Heading level={4} id={recommendationsId}>
          {t('insights.reef.recommendationsTitle')}
        </Heading>
        {insight.recommendations.length > 0 ? (
          <div lang={insightLanguage}>
            <InsightChips recommendations={insight.recommendations} onExplain={setExplainIndex} />
          </div>
        ) : (
          <Text size="sm" color="secondary">
            {t('insights.reef.noRecommendations')}
          </Text>
        )}
      </Stack>

      <ReefSuggestionList aquariumId={aquariumId} />

      {result.insightId ? (
        <AiFeedbackControl
          outputType="reef_intelligence_card"
          outputId={result.insightId}
          aquariumId={aquariumId}
        />
      ) : null}

      <ExplainabilityModal
        recommendation={explainIndex === null ? null : insight.recommendations.at(explainIndex) ?? null}
        onClose={() => setExplainIndex(null)}
      />
    </Stack>
  );
}

/**
 * Reef Intelligence dashboard card (#1384): the aquarium's AI insight with
 * enforced vs advisory recommendations, explanations, and the explicit
 * suggestion → task actions. Renders loading, error, not-configured,
 * no-ICP-data and populated states, followed by the newest AI lighting
 * evaluation (LightingInsightSection) when the tank has one.
 */
export function ReefInsightCard({ aquariumId }: ReefInsightCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const headingId = useId();
  const { data, isLoading, error, retry, refresh, isRefreshing, refreshError } = useReefInsight(aquariumId);
  const { exceeded: aiCreditsUsedUp } = useAiUsage();

  // An insight from hand-test measurements alone has a summary but no ICP trends.
  const hasData =
    data !== undefined &&
    (data.insight.trends.length > 0 || data.insight.recommendations.length > 0 || data.insight.summary !== '');

  let body: ReactNode;
  if (aquariumId === undefined || isLoading) {
    body = (
      <LiveRegion busy className="rg-reef-insight-card__loading">
        <Text color="secondary">{t('insights.reef.loading')}</Text>
      </LiveRegion>
    );
  } else if (error != null && isAiLimitKind(classifyInsightError(error))) {
    // Monthly AI credits used up / app-wide AI budget reached: explain, no retry.
    body = <AiLimitErrorMessage error={error} suppressExceeded={aiCreditsUsedUp} />;
  } else if (error != null) {
    const notConfigured = classifyInsightError(error) === 'notConfigured';
    body = notConfigured ? (
      <Alert
        tone="warning"
        live="polite"
        title={t('insights.reef.notConfiguredTitle')}
        titleLevel={4}
        action={
          <Button variant="secondary" size="sm" onClick={retry}>
            {t('insights.reef.retryAction')}
          </Button>
        }
      >
        {t('insights.reef.notConfiguredDescription')}
      </Alert>
    ) : (
      <LoadErrorState
        title={t('insights.reef.errorTitle')}
        titleLevel={4}
        message={t('insights.reef.errorDescription')}
        retryLabel={t('insights.reef.retryAction')}
        onRetry={retry}
        live="polite"
      />
    );
  } else if (data === undefined || !hasData) {
    body = (
      <EmptyState
        headingLevel={4}
        icon={<FlaskConical size={32} />}
        headline={t('insights.reef.emptyTitle')}
        description={t('insights.reef.emptyDescription')}
        actionLabel={t('insights.reef.emptyAction')}
        onAction={() => navigate(`/aquariums/${aquariumId}/icp-analyses`)}
      />
    );
  } else {
    body = <PopulatedInsight
        aquariumId={aquariumId}
        result={data}
        refreshError={refreshError}
        aiCreditsUsedUp={aiCreditsUsedUp}
      />;
  }

  return (
    <Card
      as="section"
      padding="lg"
      className="rg-reef-insight-card"
      aria-labelledby={headingId}
      data-testid="reef-insight-card"
      // Target of timeline AI-insight events (/dashboard#reef-intelligence).
      id={REEF_INTELLIGENCE_ANCHOR}
      tabIndex={-1}
    >
      <Stack gap="4">
        <Inline justify="between" gap="3">
          <Inline gap="3" wrap={false}>
            <span className="rg-reef-insight-card__icon" aria-hidden="true">
              <Sparkles size={20} />
            </span>
            <Heading level={3} id={headingId}>
              {t('insights.reef.title')}
            </Heading>
          </Inline>
          {hasData && (
            <Button
              variant="secondary"
              size="sm"
              onClick={refresh}
              isLoading={isRefreshing}
              disabled={isRefreshing || aiCreditsUsedUp}
            >
              {isRefreshing ? t('insights.reef.refreshing') : t('insights.reef.refreshAction')}
            </Button>
          )}
        </Inline>
        <AiUsageMeter variant="compact" />
        {body}
        <LightingInsightSection aquariumId={aquariumId} />
        <FlowInsightSection aquariumId={aquariumId} />
      </Stack>
    </Card>
  );
}

export default ReefInsightCard;
